import React, { useEffect, useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import axios from "axios";
import "./EditTreatmentPlanModal.css";

const EditTreatmentPlanModal = ({ show, onClose, planId, onUpdated, showToast }) => {
  const [form, setForm] = useState({
    id: null,
    risk_id: null,
    title: "",
    owner: "",
    action: "Reduce",
    status: "Planned",
    justification: "",
    planned_date: "",
    completion_date: "",
    progress: 0,
    ressources: [],
    mesures: [],
  });

  const [availableRessources, setAvailableRessources] = useState([]);
  const [availableMesures, setAvailableMesures] = useState([]);

  useEffect(() => {
    if (!show || !planId) return;

    // 🔁 Fetch plan + filtered ressources/mesures
    axios.get(`/api/get_treatment_plan.php?id=${planId}`)
      .then((res) => {
        const { plan, availableRessources, availableMesures } = res.data || {};

        if (!plan) {
          showToast("Plan not found", "danger");
          return;
        }

        setForm({
          ...plan,
          ressources: plan.ressources || [],
          mesures: plan.mesures || [],
        });

        setAvailableRessources(availableRessources || []);
        setAvailableMesures(availableMesures || []);
      })
      .catch((err) => {
        showToast("Failed to load plan data", "danger");
        console.error(err);
      });

  }, [show, planId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: name === "progress" ? parseInt(value) || 0 : value,
    }));
  };

  const toggleSelection = (listName, value) => {
    setForm((prev) => {
      const updated = prev[listName].includes(value)
        ? prev[listName].filter((v) => v !== value)
        : [...prev[listName], value];
      return { ...prev, [listName]: updated };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post("/api/update_treatment_plan.php", form);
      showToast("Treatment plan updated");
      onUpdated?.();
      onClose();
    } catch (err) {
      showToast("Update failed", "danger");
      console.error(err);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="edit-treatment-modal">
      <Form onSubmit={handleSubmit}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-pencil-square me-2 text-warning" />
              Edit Treatment Plan
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} />
          </div>

          <div className="modal-body">
            {/* 📝 Title & Owner */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control name="title" value={form.title} onChange={handleChange} />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Owner</Form.Label>
                <Form.Control name="owner" value={form.owner} onChange={handleChange} />
              </div>
            </div>

            {/* 🎯 Action & Status */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Action</Form.Label>
                <Form.Select name="action" value={form.action} onChange={handleChange}>
                  <option value="Avoid">Avoid</option>
                  <option value="Reduce">Reduce</option>
                  <option value="Transfer">Transfer</option>
                  <option value="Accept">Accept</option>
                </Form.Select>
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Status</Form.Label>
                <Form.Select name="status" value={form.status} onChange={handleChange}>
                  <option value="Planned">Planned</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </Form.Select>
              </div>
            </div>

            {/* 📅 Dates */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <Form.Label>Planned Date</Form.Label>
                <Form.Control
                  type="date"
                  name="planned_date"
                  value={form.planned_date}
                  onChange={handleChange}
                />
              </div>
              <div className="col-md-6 mb-3">
                <Form.Label>Completion Date</Form.Label>
                <Form.Control
                  type="date"
                  name="completion_date"
                  value={form.completion_date}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* 🧠 Justification */}
            <Form.Group className="mb-3">
              <Form.Label>Justification</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="justification"
                value={form.justification}
                onChange={handleChange}
              />
            </Form.Group>

            {/* 📈 Progress */}
            <Form.Group className="mb-3">
              <Form.Label>Progress</Form.Label>
              <Form.Range
                name="progress"
                min={0}
                max={100}
                value={form.progress || 0}
                onChange={handleChange}
              />
              <div className="d-flex justify-content-end">
                <span className="progress-pill">{form.progress}%</span>
              </div>
            </Form.Group>

            {/* 🔗 Linked Resources */}
            {availableRessources.length > 0 && (
              <div className="mb-3">
                <Form.Label>Linked Resources</Form.Label>
                <div className="multi-checkbox-list">
                  {availableRessources.map((res, i) => (
                    <Form.Check
                      key={`res-${i}`}
                      type="checkbox"
                      label={res}
                      checked={form.ressources.includes(res)}
                      onChange={() => toggleSelection("ressources", res)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* 🛡️ Linked Measures */}
            {availableMesures.length > 0 && (
              <div className="mb-3">
                <Form.Label>Linked Measures</Form.Label>
                <div className="multi-checkbox-list">
                  {availableMesures.map((mes, i) => (
                    <Form.Check
                      key={`mes-${i}`}
                      type="checkbox"
                      label={mes}
                      checked={form.mesures.includes(mes)}
                      onChange={() => toggleSelection("mesures", mes)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="warning" type="submit">
              Save Changes
            </Button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default EditTreatmentPlanModal;
